import { cn } from "@repo/ui/lib/utils"
import * as React from "react"
import { useTranslations } from "use-intl"

type ProgressBarColor =
  | "primary"
  | "success"
  | "warning"
  | "destructive"
  | "secondary"

interface ProgressBarProps extends React.HTMLAttributes<HTMLDivElement> {
  value: number
  max?: number
  label?: string
  showPercentage?: boolean
  color?: ProgressBarColor
}

const ProgressBar = React.forwardRef<HTMLDivElement, ProgressBarProps>(
  (
    {
      className,
      value,
      max = 100,
      label,
      showPercentage = true,
      color: colorOverride,
      ...props
    },
    ref,
  ) => {
    const t = useTranslations("ui")
    const percentage = Math.min(Math.max((value / max) * 100, 0), 100)

    // Determine color based on percentage
    const getColor = (percent: number) => {
      if (percent < 25) return "destructive"
      if (percent < 50) return "warning"
      if (percent < 75) return "primary"
      return "success"
    }

    const color = colorOverride ?? getColor(percentage)

    const colorClasses = {
      primary: "bg-primary",
      success: "bg-success",
      warning: "bg-warning",
      destructive: "bg-destructive",
      secondary: "bg-secondary-foreground",
    }

    const backgroundColorClasses = {
      primary: "bg-primary-border",
      success: "bg-success-border",
      warning: "bg-warning-border",
      destructive: "bg-destructive-border",
      secondary: "bg-secondary-border",
    }

    const borderColorClasses = {
      primary: "border-primary",
      success: "border-success",
      warning: "border-warning",
      destructive: "border-destructive",
      secondary: "border-secondary-foreground",
    }

    return (
      <div className={cn("w-full space-y-2", className)} {...props}>
        <div
          ref={ref}
          className={cn(
            "relative h-6 w-full overflow-hidden rounded-full border",
            backgroundColorClasses[color],
            borderColorClasses[color],
          )}
        >
          {percentage === 0 ? (
            <div className="h-full w-full flex items-center justify-center">
              {showPercentage && (
                <span className="text-xs font-bold text-destructive">
                  {t("proposalForm.broken")}
                </span>
              )}
            </div>
          ) : (
            <div
              className={cn(
                "h-full min-w-fit pl-2 rounded-full transition-all duration-300 ease-out flex items-center justify-end pr-3",
                colorClasses[color],
              )}
              style={{ width: `${percentage}%` }}
            >
              {showPercentage && (
                <span className="text-xs font-bold text-white">
                  {Math.round(percentage)}%
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    )
  },
)
ProgressBar.displayName = "ProgressBar"

export { ProgressBar, type ProgressBarProps }
